/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

import { IVideoPlayerViewProps } from '@msdyn365-commerce-modules/video-player';
import { IVideoPlayerProps, layout } from '../definition-extensions/video-player.ext.props.autogenerated';
import { isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import { getFeatureToggleService } from '../../../services/featureToggleService';

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */

const VideoPlayerView: React.FC<IVideoPlayerViewProps & IVideoPlayerProps<{}>> = props => {
    const { isFeatureEnabled } = getFeatureToggleService(props.context.request.cookies, props.context.app.config.features);
    const isAccessibleVideoPlayerEnabled = isFeatureEnabled('accessible-video');

    const getAutoplayValue = React.useCallback((): boolean => {
        const isMobileDevice = isMobile({ variant: VariantType.Viewport, context: props.context.request }) === 'xs';

        if (!props.config.autoplay) return false;

        if (isMobileDevice && props.config.shouldDisableAutoPlayOnMobile) return false;

        return true;
    }, []);

    const getPlayer = (video_src?: string) =>
        isAccessibleVideoPlayerEnabled ? (
            props.player
        ) : (
            <video
                className='msc-videoplayer__video'
                preload='auto'
                {...(getAutoplayValue() ? { autoPlay: true } : {})}
                muted={props.config.mute}
                loop={props.config.loop}
                controls={props.config.controls}
                src={video_src}
                // @ts-ignore
                poster={props.player.props.children.props.playerData.metaData.posterframeUrl}
                playsInline
                webkit-playsinline='true'
                aria-hidden='true'
                controlsList='nodownload'
            />
        );

    // @ts-ignore
    const video_src = props.config.video._links.binaryReferences[0]['clientHref'];
    if (props.config.layout === layout.column) {
        return (
            <div className='container ms-video-player column-layout'>
                <div className='row'>
                    <div className='text-side col-12'>
                        {props.config.heading && props.config.heading.text
                            ? React.createElement(
                                  props.config.heading.tag || 'h1',
                                  {
                                      className: 'text-side__title',
                                      contentEditable: false
                                  },
                                  props.config.heading.text
                              )
                            : ''}
                        {props.config.paragraph && (
                            <Msdyn365.RichTextComponent text={`<p className='text-side__paragraph'>${props.config.paragraph}</p>`} />
                        )}
                        {props.config.cta && props.config.cta.label && props.config.cta.link ? (
                            <div className='ms-content-block__cta'>
                                <a className='msc-cta__primary' role='button' title={props.config.cta.label} href={props.config.cta.link}>
                                    {props.config.cta.label}
                                </a>
                            </div>
                        ) : null}
                    </div>
                    <div className='video-side col-12'>
                        <div id='video-player' className='video-player-container vertical' data-m-t='video-player'>
                            {getPlayer(video_src)}
                        </div>
                    </div>
                </div>
            </div>
        );
    } else {
        return (
            <div className='container ms-video-player row-layout'>
                <div className='row'>
                    <div className='text-side col-lg-5 col-12'>
                        {props.config.heading && props.config.heading.text
                            ? React.createElement(
                                  props.config.heading.tag || 'h1',
                                  {
                                      className: 'text-side__title',
                                      contentEditable: false
                                  },
                                  props.config.heading.text
                              )
                            : ''}
                        {props.config.paragraph && (
                            <Msdyn365.RichTextComponent className='text-side__paragraph' text={props.config.paragraph} />
                        )}
                        {props.config.cta && props.config.cta.label && props.config.cta.link ? (
                            <div className='ms-content-block__cta'>
                                <a className='msc-cta__primary' role='button' title={props.config.cta.label} href={props.config.cta.link}>
                                    {props.config.cta.label}
                                </a>
                            </div>
                        ) : null}
                    </div>
                    <div className='video-side col-lg-7 col-12'>
                        <div id='video-player' className='video-player-container horizontal' data-m-t='video-player'>
                            {getPlayer(video_src)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
};

export default VideoPlayerView;
